import { supabase } from '../supabase';
import { unassignAllByParent, syncAssignmentParentName } from './assetAssignments';

function toDb(v) {
  return {
    id:          v.id,
    user_id:     v.userId,
    company_id:  v.companyId || null,
    name:        v.name,
    make:        v.make        || null,
    model:       v.model       || null,
    year:        v.year        ? parseInt(v.year) : null,
    type:        v.type        || null,
    rego:        v.rego        || null,
    vin:         v.vin         || null,
    colour:      v.colour      || null,
    fuel_type:   v.fuelType    || null,
    odometer:    v.odometer    != null ? parseFloat(v.odometer) : null,
    status:      v.status      || 'Active',
    notes:       v.notes       || null,
    photos:      v.photos      || [],
    service_log: v.serviceLog  || [],
    updated_at:  new Date().toISOString(),
  };
}

export function fromDb(r) {
  return {
    id:          r.id,
    userId:      r.user_id,
    companyId:   r.company_id,
    name:        r.name,
    make:        r.make,
    model:       r.model,
    year:        r.year,
    type:        r.type,
    rego:        r.rego,
    vin:         r.vin,
    colour:      r.colour,
    fuelType:    r.fuel_type,
    odometer:    r.odometer,
    status:      r.status || 'Active',
    notes:       r.notes,
    photos:      r.photos      || [],
    serviceLog:  r.service_log || [],
    createdAt:   r.created_at,
    updatedAt:   r.updated_at,
  };
}

export async function getVehicles() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const [{ data: own }, { data: perms }] = await Promise.all([
    supabase.from('vehicles').select('*').eq('user_id', user.id)
      .order('created_at', { ascending: false }).limit(500),
    supabase.from('asset_permissions').select('asset_id')
      .eq('user_id', user.id).eq('asset_type', 'vehicle'),
  ]);

  let provisioned = [];
  if (perms?.length) {
    const ids = perms.map(p => p.asset_id);
    const { data } = await supabase.from('vehicles').select('*').in('id', ids);
    provisioned = data || [];
  }

  const seen = new Set();
  return [...(own || []), ...provisioned]
    .filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; })
    .map(fromDb);
}

export async function upsertVehicle(vehicle) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const row = { ...toDb(vehicle), user_id: user.id };
  const isNew = !row.id;
  if (isNew) delete row.id;

  if (isNew) {
    // Insert: get back only the id to avoid large-row response issues
    const { data, error } = await supabase.from('vehicles').insert(row).select('id').single();
    if (error) throw error;
    return fromDb({ ...row, id: data.id, created_at: row.updated_at });
  } else {
    // Update: omit user_id — provisioned users must not be able to change ownership
    const { user_id: _uid, ...updateRow } = row;
    const { error } = await supabase.from('vehicles').update(updateRow).eq('id', row.id);
    if (error) throw error;
    await syncAssignmentParentName('vehicle', row.id, row.name);
    return fromDb(row);
  }
}

export async function deleteVehicle(id) {
  // Storage photos are deliberately NOT deleted here — the row goes into
  // Recently Deleted for 30 days (see supabase/recently_deleted.sql),
  // restorable via restore_deleted_record(). Eagerly deleting them would
  // make a "restored" vehicle come back with broken photo links; the 30-day
  // expiry sweep is what actually deletes the files. Asset-permission
  // grants are still cleared immediately, same as before — those aren't
  // part of what a restore brings back (a known, accepted gap, same as
  // clients not re-linking unlinked machines on restore).
  await unassignAllByParent('vehicle', id);
  await supabase.from('asset_permissions').delete().eq('asset_type', 'vehicle').eq('asset_id', id);
  const { error } = await supabase.from('vehicles').delete().eq('id', id);
  if (error) throw error;
}

// ── Permissions ──────────────────────────────────────────────────────────────

export async function getVehiclePermissions(vehicleId) {
  const { data, error } = await supabase
    .from('asset_permissions')
    .select('*')
    .eq('asset_type', 'vehicle')
    .eq('asset_id', vehicleId);
  if (error) throw error;
  return data || [];
}

export async function upsertVehiclePermission(vehicleId, userId, companyId, canEdit) {
  const { error } = await supabase.from('asset_permissions').upsert({
    asset_type: 'vehicle',
    asset_id:   vehicleId,
    user_id:    userId,
    company_id: companyId,
    can_edit:   canEdit,
  }, { onConflict: 'asset_type,asset_id,user_id' });
  if (error) throw error;
}

export async function revokeVehiclePermission(vehicleId, userId) {
  const { error } = await supabase.from('asset_permissions')
    .delete()
    .eq('asset_type', 'vehicle')
    .eq('asset_id', vehicleId)
    .eq('user_id', userId);
  if (error) throw error;
}
